// Custom Dataset Management Component
// Features: Upload, List, Train with custom data

import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Database, Upload, FileSpreadsheet, Trash2, Play, CheckCircle2,
  AlertTriangle, RefreshCw, Download, FileJson, Info, Clock, User
} from 'lucide-react';
import { useApiData, useAuth } from '@/hooks';
import { useDropzone } from 'react-dropzone';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dataset Type Badge
const DatasetTypeBadge = ({ type }) => {
  const colors = {
    capacity: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    admissions: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    census: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    labor_hours: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    custom: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
  };
  
  return (
    <Badge className={`${colors[type] || colors.custom} border`}>
      {type}
    </Badge>
  );
};

// File Upload Zone
const FileUploadZone = ({ onUpload, uploading }) => {
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      onUpload(acceptedFiles[0]);
    }
  }, [onUpload]);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/json': ['.json'],
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    disabled: uploading
  });
  
  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
        isDragActive 
          ? 'border-cyan-400 bg-cyan-500/10' 
          : 'border-slate-600 hover:border-slate-500 bg-slate-800/50'
      } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      data-testid="file-upload-zone"
    >
      <input {...getInputProps()} />
      <Upload className={`h-12 w-12 mx-auto mb-4 ${isDragActive ? 'text-cyan-400' : 'text-slate-400'}`} />
      {isDragActive ? (
        <p className="text-cyan-400 font-medium">Drop the file here...</p>
      ) : (
        <>
          <p className="text-white font-medium mb-2">Drag & drop a CSV or JSON file</p>
          <p className="text-slate-400 text-sm">or click to browse</p>
        </>
      )}
    </div>
  );
};

// Dataset Card
const DatasetCard = ({ dataset, onDelete, onSelect, isSelected }) => {
  return (
    <Card className={`bg-slate-800/60 border transition-all ${
      isSelected ? 'border-cyan-500 ring-2 ring-cyan-500/30' : 'border-slate-700/50 hover:border-slate-600'
    }`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h4 className="text-white font-medium mb-1">{dataset.name}</h4>
            <div className="flex items-center gap-2 flex-wrap">
              <DatasetTypeBadge type={dataset.type} />
              <span className="text-xs text-slate-400">{dataset.record_count} records</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-slate-400 hover:text-white"
              onClick={() => onSelect(dataset.dataset_id)}
              data-testid={`select-dataset-${dataset.dataset_id}`}
            >
              <CheckCircle2 className={`h-4 w-4 ${isSelected ? 'text-cyan-400' : ''}`} />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-slate-400 hover:text-rose-400"
              onClick={() => onDelete(dataset.dataset_id)}
              data-testid={`delete-dataset-${dataset.dataset_id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="flex items-center gap-4 text-xs text-slate-400">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {new Date(dataset.uploaded_at).toLocaleDateString()}
          </span>
          <span className="flex items-center gap-1">
            <User className="h-3 w-3" />
            {dataset.uploaded_by?.split('@')[0] || 'Unknown'}
          </span>
          {dataset.used_in_training && (
            <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Used in training</Badge>
          )}
        </div>
        
        {dataset.validation_score !== undefined && (
          <div className="mt-3">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-400">Validation Score</span>
              <span className={dataset.validation_score >= 90 ? 'text-emerald-400' : 
                             dataset.validation_score >= 70 ? 'text-amber-400' : 'text-rose-400'}>
                {dataset.validation_score}%
              </span>
            </div>
            <Progress value={dataset.validation_score} className="h-1" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Component
export default function CustomDatasetManager({ token }) {
  const [activeTab, setActiveTab] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [training, setTraining] = useState(false);
  const [selectedDatasets, setSelectedDatasets] = useState([]);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    type: 'capacity',
    description: ''
  });
  
  // Fetch datasets
  const { data: datasetsData, loading: datasetsLoading, refetch: refetchDatasets } = useApiData(
    '/api/datasets/list',
    { cache: false }
  );
  
  // Fetch analytics
  const { data: analyticsData, loading: analyticsLoading, refetch: refetchAnalytics } = useApiData(
    '/api/datasets/analytics',
    { cache: false }
  );
  
  const datasets = datasetsData?.datasets || [];
  const analytics = analyticsData || {};
  
  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dataset_name', uploadForm.name || file.name.split('.')[0]);
      formData.append('dataset_type', uploadForm.type);
      
      const response = await fetch(`${API_URL}/api/datasets/upload/file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Dataset uploaded: ${result.record_count} records`);
        refetchDatasets();
        refetchAnalytics();
        setUploadForm({ name: '', type: 'capacity', description: '' });
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload dataset');
    } finally {
      setUploading(false);
    }
  };
  
  // Handle JSON data upload
  const handleJsonUpload = async () => {
    if (!uploadForm.name) {
      toast.error('Please enter a dataset name');
      return;
    }
    
    const jsonInput = document.getElementById('json-input');
    if (!jsonInput?.value) {
      toast.error('Please enter JSON data');
      return;
    }
    
    setUploading(true);
    
    try {
      const data = JSON.parse(jsonInput.value);
      
      const response = await fetch(`${API_URL}/api/datasets/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: uploadForm.name,
          type: uploadForm.type,
          description: uploadForm.description,
          data: Array.isArray(data) ? data : [data]
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Dataset uploaded: ${result.record_count} records`);
        refetchDatasets();
        refetchAnalytics();
        jsonInput.value = '';
        setUploadForm({ name: '', type: 'capacity', description: '' });
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format');
      } else {
        console.error('Upload error:', error);
        toast.error('Failed to upload dataset');
      }
    } finally {
      setUploading(false);
    }
  };
  
  // Handle dataset selection
  const toggleDatasetSelection = (datasetId) => {
    setSelectedDatasets(prev => 
      prev.includes(datasetId) 
        ? prev.filter(id => id !== datasetId)
        : [...prev, datasetId]
    );
  };
  
  // Handle dataset deletion
  const handleDeleteDataset = async (datasetId) => {
    if (!window.confirm('Are you sure you want to delete this dataset?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/datasets/${datasetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Dataset deleted');
        refetchDatasets();
        refetchAnalytics();
        setSelectedDatasets(prev => prev.filter(id => id !== datasetId));
      } else {
        toast.error(result.error || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete dataset');
    }
  };
  
  // Handle training with custom data
  const handleTrainWithData = async () => {
    if (selectedDatasets.length === 0) {
      toast.error('Please select at least one dataset');
      return;
    }
    
    setTraining(true);
    
    try {
      const response = await fetch(`${API_URL}/api/datasets/train`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          dataset_ids: selectedDatasets,
          weeks_of_data: 52
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Training completed with ${result.training_summary.total_data_points} data points`);
        refetchDatasets();
        refetchAnalytics();
      } else {
        toast.error(result.error || 'Training failed');
      }
    } catch (error) {
      console.error('Training error:', error);
      toast.error('Failed to run training');
    } finally {
      setTraining(false);
    }
  };
  
  // Download template
  const downloadTemplate = async (type) => {
    try {
      const response = await fetch(`${API_URL}/api/datasets/templates/${type}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const template = await response.json();
      
      const blob = new Blob([JSON.stringify(template.example, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_template.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Database className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total Datasets</p>
                <p className="text-2xl font-bold text-white">{analytics.total_datasets || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Used in Training</p>
                <p className="text-2xl font-bold text-white">{analytics.datasets_used_in_training || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <FileSpreadsheet className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Utilization Rate</p>
                <p className="text-2xl font-bold text-white">{analytics.utilization_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 border-cyan-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500/20 rounded-lg">
                <Play className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Selected</p>
                <p className="text-2xl font-bold text-white">{selectedDatasets.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 border border-slate-700/50">
          <TabsTrigger value="upload" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
            <Upload className="h-4 w-4 mr-2" />
            Upload Data
          </TabsTrigger>
          <TabsTrigger value="datasets" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
            <Database className="h-4 w-4 mr-2" />
            Manage Datasets
          </TabsTrigger>
          <TabsTrigger value="train" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
            <Play className="h-4 w-4 mr-2" />
            Train Model
          </TabsTrigger>
        </TabsList>
        
        {/* Upload Tab */}
        <TabsContent value="upload" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* File Upload */}
            <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-cyan-400" />
                  Upload File
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Upload CSV or JSON files with training data
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Dataset Name</Label>
                    <Input
                      value={uploadForm.name}
                      onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                      placeholder="e.g., Q1 2025 Capacity Data"
                      className="bg-slate-800/50 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Dataset Type</Label>
                    <select
                      value={uploadForm.type}
                      onChange={(e) => setUploadForm({ ...uploadForm, type: e.target.value })}
                      className="w-full h-10 px-3 rounded-md bg-slate-800/50 border border-slate-600 text-white"
                    >
                      <option value="capacity">Capacity Data</option>
                      <option value="admissions">Admissions Data</option>
                      <option value="census">Census Data</option>
                      <option value="labor_hours">Labor Hours</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>
                
                <FileUploadZone onUpload={handleFileUpload} uploading={uploading} />
                
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => downloadTemplate('capacity')} className="text-slate-300 border-slate-600">
                    <Download className="h-3 w-3 mr-1" /> Capacity Template
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadTemplate('admissions')} className="text-slate-300 border-slate-600">
                    <Download className="h-3 w-3 mr-1" /> Admissions Template
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadTemplate('census')} className="text-slate-300 border-slate-600">
                    <Download className="h-3 w-3 mr-1" /> Census Template
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* JSON Upload */}
            <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileJson className="h-5 w-5 text-amber-400" />
                  Paste JSON Data
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Directly paste JSON array data for upload
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300">JSON Data</Label>
                  <textarea
                    id="json-input"
                    rows={8}
                    placeholder='[{"clinician_name": "Jane Smith", "hours_worked": 32, "visits_completed": 18}]'
                    className="w-full p-3 rounded-md bg-slate-800/50 border border-slate-600 text-white text-sm font-mono"
                  />
                </div>
                
                <Button 
                  onClick={handleJsonUpload} 
                  disabled={uploading}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  data-testid="upload-json-btn"
                >
                  {uploading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  Upload JSON Data
                </Button>
              </CardContent>
            </Card>
          </div>
          
          {/* Upload Info */}
          <Alert className="bg-blue-500/10 border-blue-500/30">
            <Info className="h-4 w-4 text-blue-400" />
            <AlertTitle className="text-blue-400">Dataset Types</AlertTitle>
            <AlertDescription className="text-slate-300">
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                <li><strong>Capacity:</strong> RN capacity data (clinician_name, hours_worked, visits_completed)</li>
                <li><strong>Admissions:</strong> Weekly admissions/discharges (week, admissions, discharges)</li>
                <li><strong>Census:</strong> Patient census data (week, total_patients)</li>
                <li><strong>Labor Hours:</strong> Weekly labor hours (week_start, fte_hours, prn_hours)</li>
              </ul>
            </AlertDescription>
          </Alert>
        </TabsContent>
        
        {/* Datasets Tab */}
        <TabsContent value="datasets" className="mt-6">
          <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <Database className="h-5 w-5 text-cyan-400" />
                  Your Datasets
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Manage and select datasets for model training
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { refetchDatasets(); refetchAnalytics(); }}
                className="text-slate-300 border-slate-600"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {datasetsLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-32 bg-slate-800/50 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : datasets.length === 0 ? (
                <div className="text-center py-12">
                  <Database className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No datasets uploaded yet</p>
                  <p className="text-sm text-slate-500">Upload data to enhance model training</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {datasets.map(dataset => (
                    <DatasetCard
                      key={dataset.dataset_id}
                      dataset={dataset}
                      onDelete={handleDeleteDataset}
                      onSelect={toggleDatasetSelection}
                      isSelected={selectedDatasets.includes(dataset.dataset_id)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Train Tab */}
        <TabsContent value="train" className="mt-6">
          <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Play className="h-5 w-5 text-emerald-400" />
                Train with Custom Data
              </CardTitle>
              <CardDescription className="text-slate-400">
                Run model training using selected custom datasets combined with historical data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {selectedDatasets.length === 0 ? (
                <Alert className="bg-amber-500/10 border-amber-500/30">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <AlertTitle className="text-amber-400">No Datasets Selected</AlertTitle>
                  <AlertDescription className="text-slate-300">
                    Go to the "Manage Datasets" tab and select datasets to use for training.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <h4 className="text-white font-medium mb-3">Selected Datasets ({selectedDatasets.length})</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedDatasets.map(id => {
                        const ds = datasets.find(d => d.dataset_id === id);
                        return ds ? (
                          <Badge key={id} className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                            {ds.name} ({ds.record_count} records)
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <Button
                      onClick={handleTrainWithData}
                      disabled={training}
                      className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                      data-testid="train-model-btn"
                    >
                      {training ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Training in Progress...
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Start Training Session
                        </>
                      )}
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={() => setSelectedDatasets([])}
                      className="text-slate-300 border-slate-600"
                    >
                      Clear Selection
                    </Button>
                  </div>
                  
                  <Alert className="bg-blue-500/10 border-blue-500/30">
                    <Info className="h-4 w-4 text-blue-400" />
                    <AlertDescription className="text-slate-300">
                      Training will combine your custom datasets with 52 weeks of historical data from the database.
                      This enhances prediction accuracy with your organization-specific patterns.
                    </AlertDescription>
                  </Alert>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
